import React, { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { DashboardData } from "../../../models/sales_model";
import { formatNumber } from "../../../middleware/common-functions";
import { lang } from "../../../middleware/lang";

type SalesResumeProps = {
  dashboard: DashboardData | null;
  startDate: Date | null | undefined;
  endDate: Date | null | undefined;
  onChange(
    date: Date | [Date, Date] | null,
    event: React.SyntheticEvent<any> | undefined
  ): void;
};

function formatDateShort(d: Date | null | undefined): string {
  if (!d) return "";
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const SalesResume: React.FC<SalesResumeProps> = ({
  dashboard,
  startDate,
  endDate,
  onChange,
}) => {
  const [showCalendar, setShow] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const dateLabel = (() => {
    const s = formatDateShort(startDate);
    const e = formatDateShort(endDate);
    if (!s) return lang("Seleccionar fecha", "Select date");
    if (s === e || !e) return s;
    return `${s} - ${e}`;
  })();

  return (
    <div className="sales-overview">
      {/* Header: title + date picker */}
      <div className="sales-overview-header">
        <h4 className="sales-overview-title mb-0">
          {lang("Resumen de Ventas", "Sales Overview")}
        </h4>
        <div className="sales-date-picker-wrap" ref={pickerRef}>
          <button
            className="sales-date-btn"
            onClick={() => setShow(!showCalendar)}
          >
            <i className="far fa-calendar-alt"></i>
            <span>{dateLabel}</span>
            <i className={`fas fa-chevron-${showCalendar ? "up" : "down"} fa-xs`}></i>
          </button>
          {showCalendar && (
            <div className="sales-date-dropdown">
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={(dates: any) => {
                  onChange(dates, undefined);
                  // Close picker when both dates selected
                  if (Array.isArray(dates) && dates[0] && dates[1]) {
                    setShow(false);
                  }
                }}
                selectsRange
                inline
              />
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard ? (
        <div className="kpi-row">
          {/* Hero card — Total Revenue */}
          <div className="kpi-card kpi-card--hero">
            <span className="kpi-card__label">
              {lang("Ingresos Totales", "Total Revenue")}
            </span>
            <span className="kpi-card__value kpi-card__value--accent">
              ${formatNumber(dashboard.totalRevenue)}
            </span>
            <div className="kpi-card__footer">
              <span className="kpi-card__sub">
                {dashboard.posSales.count + dashboard.onlineSales.count}{" "}
                {lang("ordenes", "orders")}
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Total de Ordenes", "Total Orders")}
            </span>
            <span className="kpi-card__value">{dashboard.totalOrders}</span>
            <div className="kpi-card__footer">
              <span className="kpi-card__sub kpi-card__sub--pos">
                POS: {dashboard.posSales.count}
              </span>
              <span className="kpi-card__sub kpi-card__sub--online">
                Online: {dashboard.onlineSales.count}
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Ticket Promedio", "Avg Ticket")}
            </span>
            <span className="kpi-card__value">
              ${formatNumber(dashboard.avgTicket)}
            </span>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Ventas POS", "POS Sales")}
            </span>
            <span className="kpi-card__value">
              ${formatNumber(dashboard.posSales.total)}
            </span>
            <div className="kpi-card__footer">
              <span className="kpi-card__sub">
                {dashboard.posSales.count} {lang("ordenes", "orders")}
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Ventas Online", "Online Sales")}
            </span>
            <span className="kpi-card__value">
              ${formatNumber(dashboard.onlineSales.total)}
            </span>
            <div className="kpi-card__footer">
              <span className="kpi-card__sub">
                {dashboard.onlineSales.count} {lang("ordenes", "orders")}
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Propinas", "Tips")}
            </span>
            <span className="kpi-card__value">
              ${formatNumber(dashboard.totalTips)}
            </span>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Envios", "Delivery Fees")}
            </span>
            <span className="kpi-card__value">
              ${formatNumber(dashboard.totalDeliveryFees)}
            </span>
          </div>

          <div className="kpi-card">
            <span className="kpi-card__label">
              {lang("Canceladas", "Cancelled")}
            </span>
            <span className="kpi-card__value kpi-card__value--danger">
              {dashboard.cancelledOrders}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesResume;
